import { PayloadAction, createSlice } from 'ngrx-slice';

import {
  MerchantResponse,
  PageSiteMerchantTypesResponse,
  PageableObject,
  PagedModelMerchantResponse,
  SortObject,
} from '@app/api/marketplace/models';
import { paginatorConfiguration } from '@shared/constants/paginator-configuration';
import { PageRequest } from '@shared/interfaces/page-request.interface';
import { PageResponse } from '@shared/interfaces/page-response.interface';

export const initialDataState = {
  totalPages: 0,
  totalElements: 0,
  size: 0,
  content: [],
  number: 0,
  sort: {} as SortObject,
  pageable: {} as PageableObject,
  first: true,
  last: true,
  numberOfElements: 0,
  empty: true,
};

export interface State {
  data: PageResponse<MerchantResponse>;
  dataLoadError: boolean;
  dataLoading: boolean;
  totalData: number;
  filter: PageRequest;
  searchText: string;
  impersonatedMerchantId: string;
}

export const initialState: State = {
  data: initialDataState,
  dataLoadError: false,
  dataLoading: false,
  totalData: 0,
  filter: {
    page: paginatorConfiguration.defaultPageIndex,
    size: paginatorConfiguration.pageSizeOptions[0],
  },
  searchText: '',
  impersonatedMerchantId: '',
};

export const {
  actions: ImpersonationActions,
  selectors,
  ...ImpersonationFeature
} = createSlice({
  name: 'impersonation',
  initialState,
  reducers: {
    loadMerchants: {
      trigger: (state) => {
        state.dataLoading = true;
      },
      success: (
        state,
        action: PayloadAction<{
          response: PagedModelMerchantResponse;
        }>
      ) => {
        state.data.content = action.response._embedded?.merchants ?? [];
        state.data.size = action.response.page?.size ?? 0;
        state.data.number = action.response.page?.number ?? 0;
        state.data.totalElements = action.response.page?.totalElements ?? 0;
        state.data.totalPages = action.response.page?.totalPages ?? 0;
        state.data.pageable = {
          pageNumber: action.response.page?.number ?? 0,
          pageSize: action.response.page?.size ?? 0,
        };
        action.response.page?.totalPages ?? 0;
        state.totalData = action.response.page?.totalElements ?? 0;
        state.dataLoading = false;
        state.dataLoadError = false;
      },
      failure: (state) => ({
        ...state,
        dataLoading: false,
        dataLoadError: true,
      }),
    },
    updateFilter: {
      trigger: (
        state,
        action: PayloadAction<{
          filter: { page?: number; size?: number; sort?: Array<string> };
        }>
      ) => {
        state.filter = action.filter;
      },
      success: () => {},
    },
    updateSearchText: {
      trigger: (
        state,
        action: PayloadAction<{
          searchText: string;
        }>
      ) => {
        state.searchText = action.searchText;
      },
      success: () => {},
    },
    impersonateMerchant: {
      trigger: (
        state,
        action: PayloadAction<{
          merchantId: string;
        }>
      ) => {
        state.impersonatedMerchantId = action.merchantId;
      },
      success: () => {},
    },
    setMerchantId: {
      trigger: (
        state,
        action: PayloadAction<{
          merchantId: string;
        }>
      ) => {
        state.impersonatedMerchantId = action.merchantId;
      },
      success: () => {},
    },
    restartImpersonation: {
      trigger: () => {},
      success: () => {},
    },
    removeImpersonation: {
      trigger: (state, action: PayloadAction<{ noRefresh?: boolean }>) => {},
      success: (state) => {
        state.impersonatedMerchantId = '';
      },
    },
    reset: (_) => initialState,
  },
});

export const ImpersonationSelectors = {
  ...selectors,
};
